import { flatMap } from 'lodash';
import moment from 'moment/moment';
import {
  ChangeDetectorRef,
  Component,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  BlockUIModule,
  ButtonModule,
  CheckboxModule,
  DialogModule,
  MessageService,
  PaginatorModule,
  PanelModule,
  TableModule,
  TooltipModule,
} from 'primeng';
import { INgxGalleryImage } from 'src/app/user-controls/ngx-gallery/ngx-gallery-image.model';
import { EventService, FileStorageService, JobsService, TaskService } from '../../../../services';
import { OrganisationTypeEnum } from '../../../../enums/organisation-types';
import { Message } from '../../../../classes/Message';
import {
  JobScreenCarryOverJob,
  JobScreenCarryOverOverNightParkingTaskStep,
  JobScreenCarryOverTask,
  JobScreenCarryOverTaskCheck,
} from '../../../../interfaces/screen-specific/job-screen';
import { RefreshJobEventTypeEnum } from '../../../../enums/refresh-job-event-type.enum';
import { Subscription } from 'rxjs';
import { NgForOf, NgIf } from '@angular/common';
import { VmePipesModule } from '../../../../vme-pipes.module';
import { VmeCommonModule } from '../../../../vme-common.module';
import { TaskSupplierScheduleStore } from '../../../task-supplier-schedule/task-supplier-schedule-store';
import { CarryoverCheckStatus } from '../../../../enums/carryover-check-status';
import { CarryoverApprovalStatus } from '../../../../enums/carryover-approval-status.enum';
import { CarryoverApprovalAnalysisComponent } from './carryover-approval-analysis/carryover-approval-analysis.component';
import { CarryoverUtils } from './carryover-utils';
import { CarryoverApprovalStatusPillComponent } from './carryover-approval-status-pill/carryover-approval-status-pill.component';
import { VehicleRegComponent } from '../../../vehicle-reg/vehicle-reg.component';
import {
  CarryoverApprovalService,
  SetCarryoverApprovalStatusRequest,
} from './carryover-approval.service';
import { CarryoverTaskInfoComponent } from './carryover-task-info.component';

@Component({
  selector: 'job-screen-carry-over',
  templateUrl: './job-screen-carry-over.component.html',
  styleUrls: ['./job-screen-carry-over.component.scss', './carryover-pill.scss'],
  standalone: true,
  imports: [
    PanelModule,
    TooltipModule,
    TableModule,
    NgIf,
    VmePipesModule,
    NgForOf,
    CheckboxModule,
    DialogModule,
    PaginatorModule,
    ButtonModule,
    BlockUIModule,
    VmeCommonModule,
    CarryoverApprovalAnalysisComponent,
    CarryoverApprovalStatusPillComponent,
    VehicleRegComponent,
    CarryoverTaskInfoComponent,
  ],
})
export class JobScreenCarryOverComponent implements OnInit, OnDestroy {
  @Input('jobId') jobId!: number;
  @Input('organisationId') supplierId!: number;
  @Input('organisationTypeId') organisationTypeId!: number;
  @Input() autoSave: boolean = false; // Flag to control auto-save behavior

  carryOverTask: JobScreenCarryOverTask;
  public showCarryOverTasks = null;
  public carryOverTasks: JobScreenCarryOverTask[] = [];
  public carryOverComment = '';
  public carryoverStatusChangeReason = '';
  public showCarryOverDialog = false;
  public loadingPopupPhoto = false;
  public popupPhotoUrl: string;
  public isShownPopupPhoto = false;
  public allowClosePopupPhoto = false;
  public triggerCarryOver = false;
  public overnightImages: INgxGalleryImage[] = [];
  public showImage = false;
  public overnightGalleryOptions = [
    {
      thumbnails: false,
      lazyLoading: false,
      image: false,
      startIndex: 0,
      showPreview: true,
      previewZoom: true,
      preview: true,
      zoomInIcon: 'fa fa-plus',
      zoomOutIcon: 'fa fa-minus',
      rotateLeftIcon: 'fa fa-rotate-left',
      rotateRightIcon: 'fa fa-rotate-right',
      rotate: true,
      previewDescription: true,
    },
  ];
  public job: JobScreenCarryOverJob;
  loadingConditions = true;
  carryOverLocation = CarryoverLocation.SupplierHome;

  subscriptions: Subscription[] = [];

  constructor(
    private jobService: JobsService,
    private taskService: TaskService,
    private messageService: MessageService,
    private eventService: EventService,
    private fileStorageService: FileStorageService,
    private carryoverApprovalService: CarryoverApprovalService,
    private cdr: ChangeDetectorRef,
  ) {
    this.subscriptions.push(
      this.eventService.JobScreenEvent.subscribe((event: any) => {
        this.handleEvents(event);
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((x) => x?.unsubscribe());
  }

  async ngOnInit() {
    try {
      await this.getJob(this.jobId);
    } catch (error) {
      console.error('Failed to fetch job:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Unable to load job details.',
      });
    }
  }

  initCarryOverTasks(job: JobScreenCarryOverJob): void {
    this.carryOverTasks = flatMap(job?.orders, (o) =>
      o?.tasks.map((t) => ({
        ...t,
        order: o,
        // Photos come from two tables while both pause photo endpoints are live
        pausePhotos: [
          ...(t.overNightParkingPhotoTaskSteps ?? []),
          ...(t.taskStepPausePhotos ?? []),
        ],
      })),
    );
  }

  private handleEvents(event: any): void {
    if (event.event === 'refresh-supplier') {
      void this.getJob(this.jobId).catch((error) => {
        console.error('Failed to refresh job:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Unable to refresh supplier job.',
        });
      });
    }
  }

  private getOverNightParkingPhotoDescriptionData(
    overnightParkingPhotoTaskStep: JobScreenCarryOverOverNightParkingTaskStep,
  ) {
    let areaTitle: string = '';
    switch (overnightParkingPhotoTaskStep.overNightParkingPhotoType) {
      case 0:
        areaTitle = 'Vehicle Parking';
        break;
      case 1:
        areaTitle = 'OverNight Odometer';
        break;
      default:
        areaTitle = 'Resume Odometer';
        break;
    }

    return {
      areaTitle,
      dateTime: overnightParkingPhotoTaskStep.dateTime,
      isSignature: false,
    };
  }

  changeCarryoverApprovalStatus() {
    if (this.carryOverLocation == 1 && this.carryOverTask?.carryOverAuthorised) {
      this.carryOverComment = "At driver's home";
    }
    if (this.autoSave) {
      this.saveCarryOver();
    }
    this.triggerCarryOver = true;
    this.showCarryOverDialog = false;
  }

  public saveCarryOver() {
    let request: SetCarryoverApprovalStatusRequest = {
      taskId: this.carryOverTask.id,
      carryoverApprovalStatus: this.newCarryoverApprovalStatus,
      reason: this.carryoverStatusChangeReason,
    };

    this.carryoverApprovalService.setCarryoverApprovalStatus(request).then((res) => {
      this.messageService.add(Message.Success('Carry over note saved'));

      // update in the UI without page refresh
      if (!this.carryOverTask.carryoverApproval) {
        this.carryOverTask.carryoverApproval = {
          taskId: this.carryOverTask.id,
          supplierId: this.supplierId,
          status: this.newCarryoverApprovalStatus,
          dateUpdated: moment().toISOString(),
        };
      }

      this.carryOverTask.carryoverApproval.status = this.newCarryoverApprovalStatus;
      this.carryOverTask.carryoverApproval.dateUpdated = moment().toISOString();
      this.carryOverTask.carryoverApproval.updatedByUserName = 'Me';
      this.carryOverTask.carryoverApproval.reason = this.carryoverStatusChangeReason;

      this.eventService.JobScreenEvent.emit({
        event: 'job-details-changed',
        data: { jobId: this.jobId },
        sender: 'saveCarryOver',
      });

      this.eventService.RefreshJobEvent.emit(RefreshJobEventTypeEnum.RefreshHistory);
    });
  }

  cancelCarryOver() {
    this.showCarryOverDialog = false;
    this.revertApprovalStatus();
  }

  hideCarryOver() {
    if (this.triggerCarryOver) {
      this.triggerCarryOver = false;
      return;
    }
    this.showCarryOverDialog = false;
    this.revertApprovalStatus();
  }

  private revertApprovalStatus() {
    const tasks = [...this.carryOverTasks];
    this.carryOverTasks = [];
    this.cdr.detectChanges();
    this.carryOverTasks = tasks;
    this.cdr.detectChanges();
  }

  canNotChangeCarryOver(data: JobScreenCarryOverTask) {
    return this.organisationTypeId != OrganisationTypeEnum.Engineius;
  }

  @HostListener('document:click', ['$event'])
  documentClick(event: MouseEvent) {
    if (this.allowClosePopupPhoto) {
      this.isShownPopupPhoto = false;
      this.loadingPopupPhoto = false;
      this.allowClosePopupPhoto = false;
    }
  }

  openPhoto(
    overNightParkingPhotoTaskSteps: Array<JobScreenCarryOverOverNightParkingTaskStep>,
    index: number,
  ) {
    this.overnightImages = overNightParkingPhotoTaskSteps.map((x) => ({
      url: x.photoPublicUrl,
      extension: x.photoExtension,
      big: x.photoPublicUrl,
      medium: x.photoPublicUrl,
      small: x.photoPublicUrl,
      key: x.photoLocation,
      descriptionData: this.getOverNightParkingPhotoDescriptionData(x),
    }));

    if (this.overnightGalleryOptions.length > 0) {
      this.overnightGalleryOptions[0].startIndex = index;
    }

    this.showImage = true;
  }

  onOverNightImagePreviewClose() {
    this.overnightImages = [];
    this.showImage = false;
  }

  private async getJob(jobId: number): Promise<void> {
    const res = this.jobService.getForComponent(jobId, 'job-screen-carry-over').then((res) => {
      this.showCarryOverTasks = true;
      this.job = res as JobScreenCarryOverJob;

      this.initCarryOverTasks(this.job);

      this.carryoverApprovalService
        .checkCarryOverConditions(this.jobId, this.supplierId)
        .then((res) => {
          this.loadingConditions = false;
          if (res && res.length > 0) {
            res.forEach((taskCheck) => {
              const task = this.carryOverTasks.find((y) => y.id === taskCheck.taskId);
              task.preApproveCarryover = taskCheck;
            });
          }
        });
    });
  }

  typedTaskInfo(item: JobScreenCarryOverTask): JobScreenCarryOverTask {
    return item;
  }

  missingConditions(preApproveCarryover: JobScreenCarryOverTaskCheck) {
    return preApproveCarryover.steps.filter((x) => x.status == CarryoverCheckStatus.Failed).length;
  }

  canSaveCarryOver() {
    if (
      this.newCarryoverApprovalStatus == CarryoverApprovalStatus.Requested ||
      this.newCarryoverApprovalStatus == CarryoverApprovalStatus.ManuallyRejected ||
      this.newCarryoverApprovalStatus == CarryoverApprovalStatus.NotRequested
    ) {
      // carryover location not needed for these statuses.
      return true;
    }

    return (
      this.carryOverLocation == CarryoverLocation.SupplierHome ||
      (this.carryOverLocation == CarryoverLocation.Other && this.carryOverComment.length > 0)
    );
  }

  newCarryoverApprovalStatus: CarryoverApprovalStatus = null;

  onStatusChange(data: JobScreenCarryOverTask, event: any) {
    this.newCarryoverApprovalStatus = event.value;
    this.carryOverTask = data;

    this.carryOverLocation =
      data.carryoverAddress == null || data.isCarryoverAddressSupplierHome
        ? CarryoverLocation.SupplierHome
        : CarryoverLocation.Other;
    this.carryOverComment = '';
    if (!data.isCarryoverAddressSupplierHome) {
      this.carryOverComment = data.carryoverAddress?.addressLine ?? '';
    }

    this.showCarryOverDialog = true;
  }

  protected readonly CarryoverApprovalStatus = CarryoverApprovalStatus;
  protected readonly CarryoverUtils = CarryoverUtils;
  protected readonly CarryoverLocation = CarryoverLocation;
}

export enum CarryoverLocation {
  SupplierHome = 1,
  Other = 2,
}
